// Detail drawer for a unified-calendar event: what it is, when, its status,
// where it came from, a countdown for deadline types, and the safe actions
// (deep link into the owning module; cancel for own meetings). All content
// here was already permission-filtered server-side by the gateway aggregation.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/features/shared/components/ui/sheet';
import { ExternalLink, MapPin, Pencil, User, Video, X } from 'lucide-react';
import {
  CALENDAR_TYPE_META, countdownLabel, joinUrl as resolveJoinUrl,
  meetingProviderLabel, detectMeetingProvider, type WorkforceCalendarEvent,
} from '@/lib/calendarApi';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', pending: 'Pending', scheduled: 'Scheduled', approved: 'Approved',
  completed: 'Completed', cancelled: 'Cancelled', rejected: 'Rejected',
  at_risk: 'At risk', overdue: 'Overdue',
};
const STATUS_CHIP: Record<string, string> = {
  overdue: 'bg-red-100 text-red-700 dark:bg-red-500/[0.18] dark:text-red-300',
  at_risk: 'bg-amber-100 text-amber-700 dark:bg-amber-500/[0.16] dark:text-amber-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/[0.16] dark:text-emerald-300',
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/[0.16] dark:text-emerald-300',
  cancelled: 'bg-muted text-muted-foreground',
  rejected: 'bg-muted text-muted-foreground',
};

const SOURCE_LABEL: Record<string, string> = {
  'leave-service': 'Time off & attendance',
  'performance-service': 'Performance',
  'workassignment-service': 'Work assignments',
  'work-management-service': 'Work',
  'payroll-service': 'Payroll',
  'credit-service': 'Loans & advances',
  'learning-service': 'Learning & Development',
};

function fmtWhen(ev: WorkforceCalendarEvent): string {
  if (ev.allDay) {
    const fmt = (d: string) =>
      new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    const start = fmt(ev.startAt);
    const end = ev.endAt && ev.endAt.slice(0, 10) !== ev.startAt.slice(0, 10) ? ` – ${fmt(ev.endAt)}` : '';
    return `${start}${end}`;
  }
  const start = new Date(ev.startAt);
  const when = start.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const end = ev.endAt ? ` – ${new Date(ev.endAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}` : '';
  return `${when}${end}`;
}

interface Props {
  event: WorkforceCalendarEvent | null;
  onClose: () => void;
  onCancelMeeting?: (id: string) => void;
  onEditMeeting?: (event: WorkforceCalendarEvent) => void;
}

export const EventDetailSheet: React.FC<Props> = ({ event, onClose, onCancelMeeting, onEditMeeting }) => {
  const navigate = useNavigate();
  if (!event) return null;
  const meta = CALENDAR_TYPE_META[event.type];
  const countdown = meta?.deadline ? countdownLabel(event.startAt) : null;
  const isOrganizer = (event.availableActions || []).some((a) => a.id === 'cancel');
  const canCancel = Boolean(onCancelMeeting && event.type === 'meeting' && isOrganizer);
  const canEdit = Boolean(onEditMeeting && event.type === 'meeting' && isOrganizer);
  // Prefer the structured join URL (synced external meetings carry meetingUrl +
  // a 'join' action); fall back to a URL in the location (Meet/Teams/Zoom/…).
  const joinUrl = resolveJoinUrl(event)
    || (/^https?:\/\//i.test(event.location || '') ? event.location : undefined);
  // Prefer the provider the sync layer tagged (external events); otherwise infer
  // it from the join URL so an internal meeting with a pasted Meet/Teams/Zoom
  // link gets the same badge, not just the Join button.
  const providerLabel = meetingProviderLabel(event.meetingProvider)
    || meetingProviderLabel(detectMeetingProvider(joinUrl) || undefined);
  // "Open in module" is pointless when the deep link is the calendar itself.
  const moduleLink = event.deepLink && event.deepLink !== '/app/my-calendar' ? event.deepLink : undefined;

  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${meta.chip}`}>
              <span className={`w-2 h-2 rounded-full ${meta.dot}`} aria-hidden /> {meta.label}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CHIP[event.status] || 'bg-muted text-foreground'}`}>
              {STATUS_LABEL[event.status] || event.status}
            </span>
            {providerLabel && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
                <Video className="w-3 h-3" /> {providerLabel}
              </span>
            )}
          </div>
          <SheetTitle className="text-left">{event.title}</SheetTitle>
          <SheetDescription className="text-left">{fmtWhen(event)}</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3 text-sm">
          {countdown && (
            <div className={`font-medium ${event.status === 'overdue' ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
              {countdown}
            </div>
          )}
          {typeof event.progress === 'number' && (
            <div>
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Progress</span><span>{Math.round(event.progress)}%</span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, Math.max(0, event.progress))}%` }} />
              </div>
            </div>
          )}
          {event.description && <p className="text-muted-foreground">{event.description}</p>}
          {event.personName && (
            <div className="flex items-center gap-2 text-foreground"><User className="w-4 h-4 text-muted-foreground" /> {event.personName}</div>
          )}
          {event.location && (
            <div className="flex items-center gap-2 text-foreground"><MapPin className="w-4 h-4 text-muted-foreground" /> {event.location}</div>
          )}
          {Array.isArray(event.metadata?.legalEntities) && (event.metadata.legalEntities as string[]).length > 0 && (
            <div className="text-sm text-foreground">
              <span className="text-muted-foreground">Entities: </span>
              {(event.metadata.legalEntities as string[]).join(', ')}
            </div>
          )}
          {event.type === 'external' && event.metadata?.responseStatus ? (
            <div className="text-xs text-muted-foreground">
              Your response: <span className="text-foreground capitalize">{String(event.metadata.responseStatus).replace('_', ' ')}</span>
            </div>
          ) : null}
          <div className="text-xs text-muted-foreground">
            {event.type === 'external'
              ? `Calendar source: ${event.metadata?.calendarSource === 'microsoft' ? 'Microsoft 365' : 'Google Calendar'}`
              : `Source: ${event.type === 'meeting' ? 'Meetings' : SOURCE_LABEL[event.sourceService] || event.sourceService}`}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {joinUrl && (
            <a
              href={joinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-orange-600 text-white hover:bg-orange-700 text-sm font-medium"
            >
              <Video className="w-4 h-4" /> Join Meeting
            </a>
          )}
          {moduleLink && (
            <button
              type="button"
              onClick={() => { onClose(); navigate(moduleLink); }}
              className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium ${joinUrl ? 'border border-border text-foreground hover:bg-muted' : 'bg-orange-600 text-white hover:bg-orange-700'}`}
            >
              <ExternalLink className="w-4 h-4" /> Open in module
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => { onEditMeeting!(event); onClose(); }}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-foreground hover:bg-muted text-sm font-medium"
            >
              <Pencil className="w-4 h-4" /> Edit
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              onClick={() => { onCancelMeeting!(event.sourceId); onClose(); }}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-foreground hover:bg-muted text-sm font-medium"
            >
              <X className="w-4 h-4" /> Cancel meeting
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default EventDetailSheet;
